import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, orgID, useData } from '@yingbaba/api';
import { Button, ErrorNotice, Notice, QueryState, useToast } from '@yingbaba/ui';
import { Link } from 'react-router-dom';
import type { BusinessEmailPreferences } from '@yingbaba/types';

const categories = [
  ['quotes', '询价与报价', '询价联系人或组织负责人的新报价、方案、成交及询价关闭提醒。'],
  ['payments', '付款与退款', '可付款阶段、付款确认、退款完成；仅发送给有财务权限的成员。'],
  ['delivery', '交付与验收', '正式交付、验收和返工；发送给负责项目的成员。'],
  ['support', '客服与售后', '客服关键回复、补充材料提醒和争议处理结果。'],
] as const;

export function EmailPreferences({ onSaved }: { onSaved: () => void }) {
  const q = useData<BusinessEmailPreferences>('/notification-email/preferences');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const qc = useQueryClient();
  useEffect(() => {
    setSelected(q.data?.items.filter((item) => item.enabled).map((item) => item.category) || []);
  }, [q.data]);
  async function save() {
    const organization = orgID();
    setBusy(true);
    setError(null);
    try {
      const updated = await api<BusinessEmailPreferences>('/notification-email/preferences', {
        method: 'PUT',
        body: JSON.stringify({ categories: selected, email: q.data?.email }),
      });
      if (organization !== orgID()) return;
      qc.setQueryData(['/notification-email/preferences', organization], updated);
      toast('邮件设置已保存');
      onSaved();
    } catch (e) {
      if (organization === orgID()) setError(e);
    } finally {
      if (organization === orgID()) setBusy(false);
    }
  }
  return (
    <QueryState query={q}>
      {q.data ? (
        <div className="email-preferences">
          <p className="muted">
            提醒可按组织分别调整。邮件发送到 <strong>{q.data.email}</strong>。
          </p>
          {!q.data.email_verified ? (
            <Notice>
              <Link to="/account">验证登录邮箱</Link>后才会发送邮件。你可以先关闭不需要的提醒。
            </Notice>
          ) : null}
          {!q.data.available ? <Notice>业务邮件服务暂未启用，站内通知仍可正常查看。</Notice> : null}
          <div className="email-preference-options">
            {categories
              .filter(([key]) => q.data.items.some((item) => item.category === key))
              .map(([key, label, description]) => (
                <label className="email-preference-option" key={key}>
                  <input
                    type="checkbox"
                    checked={selected.includes(key)}
                    disabled={
                      busy ||
                      ((!q.data?.available || !q.data.email_verified) &&
                        !q.data.items.some((item) => item.category === key && item.enabled))
                    }
                    onChange={(event) =>
                      setSelected((values) =>
                        event.target.checked
                          ? [...values, key]
                          : values.filter((item) => item !== key),
                      )
                    }
                  />
                  <span>
                    <strong>{label}</strong>
                    <span className="muted small">{description}</span>
                  </span>
                </label>
              ))}
          </div>
          <p className="muted small">
            只发送与你的业务和权限有关的提醒。可随时在这里关闭或通过邮件退订；更换邮箱后需重新开启。账号安全邮件独立发送。
          </p>
          <ErrorNotice error={error} />
          <Button loading={busy} onClick={save}>
            保存邮件设置
          </Button>
        </div>
      ) : null}
    </QueryState>
  );
}
